import { useEffect, useState } from 'react'
import { ShieldCheck } from '@phosphor-icons/react'
import { Card, CardContent } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { useAuthStore } from '@/stores/authStore'
import { authApi, type RoleAccessMatrix } from '@/lib/api'
import { cn } from '@/lib/utils'
import { cardChromeClass } from '@/lib/styleKit'
import { iconForAccess, roleLabel } from './roleAccessDisplay'

/**
 * Settings > Roles & Access — a pricing-table-style comparison of what every
 * role in the account can do, grouped into feature areas (`GET
 * /auth/role-matrix`, derived live from `backend/app/core/permissions.py` so
 * it can't drift). Read-only, visible to every role (like TeamSection's
 * "Your role" chip): everyone can see the account's own access design, not
 * just their own slice of it.
 *
 * Your own role's column is highlighted, the other four are muted — the same
 * shape a pricing page uses to draw the eye to the plan you're actually on
 * while still showing the rest for comparison.
 */
export default function RoleAccessSection() {
  const currentUserRole = useAuthStore((s) => s.user?.role)
  const [matrix, setMatrix] = useState<RoleAccessMatrix | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    authApi.getRoleMatrix()
      .then((res) => { if (!cancelled) setMatrix(res.data) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const roles = matrix?.roles ?? []

  return (
    <div className="space-y-6">
      <div>
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
        >
          Roles & Access
        </h2>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] mt-1"
          style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
        >
          {currentUserRole
            ? `You're signed in as ${roleLabel(currentUserRole)}. Here's what every role in your account can do.`
            : 'What every role in your account can do.'}
        </p>
      </div>

      <Card className={cardChromeClass}>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="text" width="100%" height="32px" />
              ))}
            </div>
          ) : !matrix ? (
            <p className="p-4 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px' }}>
              Couldn't load role access right now.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border/50 dark:border-white/[0.08]">
                    <th
                      className="sticky left-0 bg-card dark:bg-[#191a1b] text-left px-3 py-2.5 text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap"
                      style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                    >
                      Feature
                    </th>
                    {roles.map((role) => {
                      const isYou = role === currentUserRole
                      return (
                        <th
                          key={role}
                          className={cn(
                            'px-3 py-2.5 text-center whitespace-nowrap',
                            isYou
                              ? 'bg-[#5e6ad2]/[0.08] dark:bg-[#5e6ad2]/[0.12]'
                              : 'opacity-60',
                          )}
                        >
                          {isYou && (
                            <span className="flex items-center justify-center gap-1 mb-0.5">
                              <ShieldCheck className="w-3 h-3 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                              <span
                                className="text-[#5e6ad2] dark:text-[#a5b4fc]"
                                style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                              >
                                Your role
                              </span>
                            </span>
                          )}
                          <span
                            className={cn(
                              isYou
                                ? 'text-[#5e6ad2] dark:text-[#a5b4fc]'
                                : 'text-foreground/80 dark:text-[#d0d6e0]',
                            )}
                            style={{ fontSize: '13px', fontWeight: isYou ? 590 : 510, letterSpacing: '-0.13px' }}
                          >
                            {roleLabel(role)}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {matrix.areas.map((area) => (
                    <tr key={area.key} className="border-b border-border/30 dark:border-white/[0.04] last:border-0">
                      <td
                        className="sticky left-0 bg-card dark:bg-[#191a1b] px-3 py-2.5 whitespace-nowrap text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {area.label}
                        {/* This area's access ALSO depends on the account's plan, on top of
                            whatever the role columns show — e.g. an Owner can look fully
                            capable of inviting teammates by role alone and still be refused
                            because Team invites are plan-gated too. */}
                        {area.plan_note && (
                          <span
                            className="block text-amber-700 dark:text-amber-300 mt-0.5 whitespace-normal"
                            style={{ fontSize: '10px', letterSpacing: '-0.05px', lineHeight: 1.4, maxWidth: '160px' }}
                          >
                            {area.plan_note}
                          </span>
                        )}
                      </td>
                      {roles.map((role) => {
                        const cell = area.access[role]
                        const isYou = role === currentUserRole
                        const { Icon, colorClass } = iconForAccess(cell?.icon ?? 'dash')
                        return (
                          <td
                            key={role}
                            title={cell?.note}
                            className={cn(
                              'px-3 py-2.5 text-center',
                              isYou ? 'bg-[#5e6ad2]/[0.04] dark:bg-[#5e6ad2]/[0.06]' : 'opacity-60',
                            )}
                          >
                            <Icon className={cn('w-4 h-4 inline-block', colorClass)} weight="bold" />
                            <span
                              className="block text-muted-foreground dark:text-[#8a8f98] mt-0.5 whitespace-nowrap"
                              style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                            >
                              {cell?.label ?? 'No access'}
                            </span>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
